import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../../layout/AppLayout";
import { ErpSelect, ErpTable, toOptions } from "../../components/erp";
import { errorText } from "../../api/client";
import { csDropdownsApi, ibtApi, IbtRequestRow } from "./api";

/** Port of v3/ColdStore/CsIBTRequestEntryForm.php - inter-store transfer request header list +
 * create form. Line-item breakup (CsIBTRequestBreakUpDetails, `/api/coldstore/ibt/breakup`) is
 * available server-side but its own screen is deferred this pass - see MIGRATION.md § ColdStore. */
export default function IbtTransferPage() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ IBTOrderNumber: "", OrderDate: "", FromStore: "", ToStore: "", Remarks: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dd = useQuery({ queryKey: ["coldstore", "dropdowns"], queryFn: csDropdownsApi.get, staleTime: 5 * 60_000 });
  const stores = useMemo(() => toOptions(dd.data?.Data?.ColdStores, (c) => c.StoreID, (c) => c.StoreName), [dd.data]);
  const list = useQuery({ queryKey: ["coldstore", "ibt-requests"], queryFn: () => ibtApi.list() });

  const submit = async () => {
    setSaving(true); setErr(null);
    try {
      await ibtApi.save({ ...form, RequestStatus: "Pending" });
      setForm({ IBTOrderNumber: "", OrderDate: "", FromStore: "", ToStore: "", Remarks: "" });
      qc.invalidateQueries({ queryKey: ["coldstore", "ibt-requests"] });
    } catch (e) { setErr(errorText(e)); } finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader title="Inter-Store Transfer (IBT)" location="ColdStore" />
      <div className="card mb-3"><div className="card-body">
        <div className="row">
          <div className="col-md-2 mb-3"><label className="form-label">Order No</label>
            <input className="form-control" data-testid="ibt-order-number" value={form.IBTOrderNumber} onChange={(e) => setForm((s) => ({ ...s, IBTOrderNumber: e.target.value }))} /></div>
          <div className="col-md-2 mb-3"><label className="form-label">Order Date</label>
            <input type="date" className="form-control" data-testid="ibt-order-date" value={form.OrderDate} onChange={(e) => setForm((s) => ({ ...s, OrderDate: e.target.value }))} /></div>
          <div className="col-md-2 mb-3"><label className="form-label">From Store</label>
            <ErpSelect id="ibt-from-store" value={form.FromStore} onChange={(v) => setForm((s) => ({ ...s, FromStore: v }))} options={stores} testId="ibt-from-store" /></div>
          <div className="col-md-2 mb-3"><label className="form-label">To Store</label>
            <ErpSelect id="ibt-to-store" value={form.ToStore} onChange={(v) => setForm((s) => ({ ...s, ToStore: v }))} options={stores} testId="ibt-to-store" /></div>
          <div className="col-md-2 mb-3"><label className="form-label">Remarks</label>
            <input className="form-control" data-testid="ibt-remarks" value={form.Remarks} onChange={(e) => setForm((s) => ({ ...s, Remarks: e.target.value }))} /></div>
          <div className="col-md-2 mb-3 d-flex align-items-end">
            <button className="btn btn-primary w-100" data-testid="ibt-submit-btn" disabled={saving || !form.FromStore || !form.ToStore} onClick={submit}>
              {saving ? "Saving..." : "Create Request"}</button></div>
        </div>
        {err && <div className="text-danger" data-testid="ibt-error">{err}</div>}
      </div></div>
      <div className="card"><div className="card-body">
        <ErpTable<IbtRequestRow> testId="ibt-requests-table" loading={list.isLoading} error={list.isError ? errorText(list.error) : null}
          rows={list.data?.Data || []} columns={[
            { key: "SNO", title: "#" }, { key: "IBTOrderNumber", title: "Order No" }, { key: "OrderDate", title: "Order Date" },
            { key: "FromStoreName", title: "From Store" }, { key: "ToStoreName", title: "To Store" },
            { key: "RequestStatus", title: "Status" },
          ]} />
      </div></div>
    </>
  );
}
